import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { useParams, Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { getTransaction, updateTransaction, openContainer, refundCan } from '../../actions/canTransaction';
import {
    Container, 
    Row, 
    Col, 
    Button, 
    Card, 
    Form, 
    Badge, 
    Alert, 
    Spinner,
    Toast,
    ToastContainer,
    Modal
} from "react-bootstrap";

const Transaction = ({ getTransaction, transaction, updateTransaction, openContainer, refundCan, loading }) => {
    const { id } = useParams();
    const [formData, setFormData] = useState({});
    const [newData, setNewData] = useState({});
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastVariant, setToastVariant] = useState('success');
    const [showOpenModal, setShowOpenModal] = useState(false);
    const [showRefundModal, setShowRefundModal] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const showAlert = (message, variant = 'success') => {
        setToastMessage(message);
        setToastVariant(variant);
        setShowToast(true);
    };

    useEffect( () => {
        if(transaction === undefined || transaction === null){
            getTransaction(id);
        }else {
            setFormData(transaction);
            setFormData({...transaction, ["product"]: transaction.brand});
        }
    }, [ transaction ] );

    const onChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        if(e.target.name !== "comment"){
            setNewData({ ...newData, [e.target.name]: e.target.value === "true" ? true : false})
        }else{
            setNewData({ ...newData, [e.target.name]: e.target.value})
        }
    }

    const onSubmit = async (e) => {
        e.preventDefault();
        try {
            setIsSubmitting(true);
            await updateTransaction(newData, formData._id);
            showAlert('Transaction updated successfully!', 'success');
        } catch (error) {
            showAlert('Failed to update transaction. Please try again.', 'danger');
        } finally {
            setIsSubmitting(false);
        }
    };

    const getStatusBadge = (status) => {
        switch(status?.toLowerCase()) {
            case 'success':
            case 'completed':
            case 'opened':
                return <Badge bg="success">{status}</Badge>;
            case 'pending':
                return <Badge bg="warning">{status}</Badge>;
            case 'failed':
            case 'error':
                return <Badge bg="danger">{status}</Badge>;
            case 'cancelled':
                return <Badge bg="secondary">{status}</Badge>;
            default:
                return <Badge bg="info">{status || 'Unknown'}</Badge>;
        }
    };

    const renderTransactionForm = () => {
        if(!formData) return null;

        return(
            <Card className="shadow-sm">
                <Card.Header className="bg-primary text-white">
                    <h5 className="mb-0">
                        <i className="fas fa-edit me-2"></i>
                        Transaction Details
                    </h5>
                </Card.Header>
                <Card.Body>
                    <Form onSubmit={onSubmit}>
                        <Row className="g-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <i className="fas fa-receipt me-2"></i>
                                        Transaction ID
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="transactionId"
                                        value={formData.transactionId || ""}
                                        disabled
                                        readOnly
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <i className="fas fa-box me-2"></i>
                                        Container ID
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="containerId"
                                        value={formData.containerId || ""}
                                        disabled
                                        readOnly
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <i className="fas fa-info-circle me-2"></i>
                                        Status
                                    </Form.Label>
                                    <div className="d-flex align-items-center">
                                        <Form.Control
                                            type="text"
                                            name="status"
                                            value={formData.status || ""}
                                            disabled
                                            readOnly
                                            className="me-2"
                                        />
                                        {getStatusBadge(formData.status)}
                                    </div>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <i className="fas fa-can-food me-2"></i>
                                        Product
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="product"
                                        value={formData.product || ""}
                                        disabled
                                        readOnly
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <i className="fas fa-undo me-2"></i>
                                        Return Can
                                    </Form.Label>
                                    <Form.Select
                                        name="returnCan"
                                        value={formData.returnCan}
                                        onChange={onChange}
                                        disabled={isSubmitting}
                                    >
                                        <option value="true">Yes</option>
                                        <option value="false">No</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <i className="fas fa-coins me-2"></i>
                                        Deposit Used
                                    </Form.Label>
                                    <Form.Select
                                        name="depositUsed"
                                        value={formData.depositUsed}
                                        onChange={onChange}
                                        disabled={isSubmitting}
                                    >
                                        <option value="true">Yes</option>
                                        <option value="false">No</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <i className="fas fa-cogs me-2"></i>
                                        Container Working
                                    </Form.Label>
                                    <Form.Select
                                        name="isContainerWorking"
                                        value={formData.isContainerWorking}
                                        onChange={onChange}
                                        disabled={isSubmitting}
                                    >
                                        <option value="true">Yes</option>
                                        <option value="false">No</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <i className="fas fa-door-open me-2"></i>
                                        Container Open
                                    </Form.Label>
                                    <Form.Select
                                        name="containerOpen"
                                        value={formData.containerOpen}
                                        onChange={onChange}
                                        disabled={isSubmitting}
                                    >
                                        <option value="true">Yes</option>
                                        <option value="false">No</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <i className="fas fa-door-closed me-2"></i>
                                        Container Close
                                    </Form.Label>
                                    <Form.Select
                                        name="containerClose"
                                        value={formData.containerClose}
                                        onChange={onChange}
                                        disabled={isSubmitting}
                                    >
                                        <option value="true">Yes</option>
                                        <option value="false">No</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <i className="fas fa-check-circle me-2"></i>
                                        Return Can Verified
                                    </Form.Label>
                                    <Form.Select
                                        name="returnCanVerified"
                                        value={formData.returnCanVerified}
                                        onChange={onChange}
                                        disabled={isSubmitting}
                                    >
                                        <option value="true">Yes</option>
                                        <option value="false">No</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <i className="fas fa-clock me-2"></i>
                                        Date & Time
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="time"
                                        value={formData.time ? new Date(formData.time).toLocaleString() : ""}
                                        disabled
                                        readOnly
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={12}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <i className="fas fa-comment me-2"></i>
                                        Comment
                                    </Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        name="comment"
                                        value={formData.comment || ""}
                                        onChange={onChange}
                                        placeholder="Enter your comment here..."
                                        disabled={isSubmitting}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <i className="fas fa-clock me-2"></i>
                                        Container Open Time
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="containerOpenTime"
                                        value={formData.containerOpenTime ? new Date(formData.containerOpenTime).toLocaleString() : "N/A"}
                                        disabled
                                        readOnly
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <i className="fas fa-robot me-2"></i>
                                        Vending Machine Message
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="vendingMachineMessage"
                                        value={formData.vendingMachineMessage || "N/A"}
                                        disabled
                                        readOnly
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={12} className="text-center">
                                <Button
                                    type="submit"
                                    variant="primary"
                                    size="lg"
                                    disabled={isSubmitting}
                                    className="px-4"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <Spinner size="sm" className="me-2" />
                                            Updating...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-save me-2"></i>
                                            Update Transaction
                                        </>
                                    )}
                                </Button>
                            </Col>
                        </Row>
                    </Form>
                </Card.Body>
            </Card>
        );
    }

    const renderUserData = () => {
        if(!formData.user) return (
            <Alert variant="warning" className="text-center">
                <i className="fas fa-exclamation-triangle me-2"></i>
                User information not available
            </Alert>
        );

        return (
            <Card className="shadow-sm h-100">
                <Card.Header className="bg-info text-white">
                    <h5 className="mb-0">
                        <i className="fas fa-user me-2"></i>
                        User Details
                    </h5>
                </Card.Header>
                <Card.Body className="text-center">
                    <div className="mb-3">
                        <i className="fas fa-user-circle fa-3x text-info mb-3"></i>
                    </div>
                    <div className="mb-3">
                        <strong className="text-muted">
                            <i className="fas fa-user me-2"></i>
                            Name:
                        </strong>
                        <div className="h6 mb-0">{formData.user.name}</div>
                    </div>
                    <div className="mb-3">
                        <strong className="text-muted">
                            <i className="fas fa-phone me-2"></i>
                            Phone:
                        </strong>
                        <div className="h6 mb-0">{formData.user.phone}</div>
                    </div>
                    <div className="mb-4">
                        <strong className="text-muted">
                            <i className="fas fa-envelope me-2"></i>
                            Email:
                        </strong>
                        <div className="h6 mb-0">{formData.user.email}</div>
                    </div>
                    <Button
                        as={Link}
                        to={`/inventory/v1.2/users/${formData.user._id}`}
                        variant="info"
                        size="sm"
                        className="px-3"
                    >
                        <i className="fas fa-eye me-2"></i>
                        View User Details
                    </Button>
                </Card.Body>
            </Card>
        );
    }

    const renderVendingMachineData = () => {
        if(!formData.vendingMachine) return (
            <Alert variant="warning" className="text-center">
                <i className="fas fa-exclamation-triangle me-2"></i>
                Vending machine information not available
            </Alert>
        );

        return (
            <Card className="shadow-sm h-100">
                <Card.Header className="bg-success text-white">
                    <h5 className="mb-0">
                        <i className="fas fa-cog me-2"></i>
                        Vending Machine Details
                    </h5>
                </Card.Header>
                <Card.Body className="text-center">
                    <div className="mb-3">
                        <i className="fas fa-robot fa-3x text-success mb-3"></i>
                    </div>
                    <div className="mb-3">
                        <strong className="text-muted">
                            <i className="fas fa-tag me-2"></i>
                            Name:
                        </strong>
                        <div className="h6 mb-0">{formData.vendingMachine.name}</div>
                    </div>
                    <div className="mb-3">
                        <strong className="text-muted">
                            <i className="fas fa-id-card me-2"></i>
                            Profile:
                        </strong>
                        <div className="h6 mb-0">{formData.vendingMachine.profileName || 'N/A'}</div>
                    </div>
                    <div className="mb-4">
                        <strong className="text-muted">
                            <i className="fas fa-map-marker-alt me-2"></i>
                            Region:
                        </strong>
                        <div className="h6 mb-0">{formData.vendingMachine.region || 'N/A'}</div>
                    </div>
                    <Button
                        as={Link}
                        to={`/inventory/v1.2/vendingMachine/${formData.vendingMachine._id}`}
                        variant="success"
                        size="sm"
                        className="px-3"
                    >
                        <i className="fas fa-eye me-2"></i>
                        View Machine Details
                    </Button>
                </Card.Body>
            </Card>
        );
    }

    const handleOpen = async () => {
        try {
            setIsSubmitting(true);
            await openContainer({}, formData._id);
            showAlert('Container opened successfully!', 'success');
        } catch (error) {
            showAlert('Failed to open container. Please try again.', 'danger');
        } finally {
            setIsSubmitting(false);
        }
    }

    const handleRefundCan = async () => {
        try {
            setIsSubmitting(true);
            await refundCan({}, formData._id);
            showAlert('Can refunded successfully!', 'success');
        } catch (error) {
            showAlert('Failed to refund can. Please try again.', 'danger');
        } finally {
            setIsSubmitting(false);
        }
    }

    const confirmOpenContainer = async () => {
        setShowOpenModal(false);
        await handleOpen();
    }

    const confirmRefundCan = async () => {
        setShowRefundModal(false);
        await handleRefundCan();
    }

    const renderActionCards = () => {
        const canOpen = formData.status && formData.status !== "opened";
        const canRefund = formData.status && formData.status !== "opened" && formData.status !== "failed";

        return (
            <Row className="g-3">
                <Col md={6}>
                    <Card className="shadow-sm h-100">
                        <Card.Header className="bg-warning text-dark">
                            <h5 className="mb-0">
                                <i className="fas fa-door-open me-2"></i>
                                Container Control
                            </h5>
                        </Card.Header>
                        <Card.Body className="text-center">
                            <div className="mb-3">
                                <i className="fas fa-box-open fa-3x text-warning mb-3"></i>
                            </div>
                            {canOpen ? (
                                <>
                                    <p className="text-muted mb-3">
                                        Open the container to allow access to the product.
                                    </p>
                                    <Button
                                        onClick={() => setShowOpenModal(true)}
                                        variant="warning"
                                        disabled={isSubmitting}
                                        className="px-4"
                                    >
                                        <i className="fas fa-unlock me-2"></i>
                                        Open Container
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <Badge bg="success" className="mb-3 fs-6">
                                        <i className="fas fa-check-circle me-2"></i>
                                        Already Opened
                                    </Badge>
                                    <p className="text-muted mb-0">
                                        Container has been opened successfully.
                                    </p>
                                </>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={6}>
                    <Card className="shadow-sm h-100">
                        <Card.Header className="bg-danger text-white">
                            <h5 className="mb-0">
                                <i className="fas fa-undo me-2"></i>
                                Refund Control
                            </h5>
                        </Card.Header>
                        <Card.Body className="text-center">
                            <div className="mb-3">
                                <i className="fas fa-money-bill-wave fa-3x text-danger mb-3"></i>
                            </div>
                            {canRefund ? (
                                <>
                                    <p className="text-muted mb-3">
                                        Process a refund for this transaction.
                                    </p>
                                    <Button
                                        onClick={() => setShowRefundModal(true)}
                                        variant="danger"
                                        disabled={isSubmitting}
                                        className="px-4"
                                    >
                                        <i className="fas fa-money-bill-wave me-2"></i>
                                        Process Refund
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <Badge bg="secondary" className="mb-3 fs-6">
                                        <i className="fas fa-info-circle me-2"></i>
                                        {formData.status === "opened" ? "Transaction Completed" : "Already Refunded"}
                                    </Badge>
                                    <p className="text-muted mb-0">
                                        {formData.status === "opened" 
                                            ? "Transaction has been completed successfully." 
                                            : "Refund has already been processed."}
                                    </p>
                                </>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        );
    }

    if (loading && !formData._id) {
        return (
            <Container className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <div className="mt-3">Loading transaction details...</div>
            </Container>
        );
    }

    if (!formData._id) {
        return (
            <Container className="py-5">
                <Alert variant="danger" className="text-center">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    Transaction not found or failed to load.
                </Alert>
            </Container>
        );
    }

    return (
        <Fragment>
            <Container fluid className="py-4">
                {/* Header Section */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center flex-wrap">
                            <div>
                                <Link 
                                    className="btn btn-outline-primary d-flex align-items-center mb-2" 
                                    to="/inventory/v1.2/transactions"
                                >
                                    <i className="fas fa-arrow-left me-2"></i>
                                    Back to Transactions
                                </Link>
                                <h1 className="h3 mb-2 text-gray-800 fw-bold d-flex align-items-center">
                                    <i className="fas fa-receipt me-3 text-primary" style={{fontSize: '32px'}}></i>
                                    Transaction Management
                                </h1>
                                <p className="text-muted mb-0">
                                    Manage and update transaction details
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <Row className="g-4">
                    <Col lg={8}>
                        {renderTransactionForm()}
                    </Col>
                    <Col lg={4}>
                        <div className="mb-4">
                            {renderUserData()}
                        </div>
                        <div className="mb-4">
                            {renderVendingMachineData()}
                        </div>
                    </Col>
                </Row>

                <Row className="mt-4">
                    <Col>
                        <Card className="shadow-sm">
                            <Card.Header className="bg-secondary text-white">
                                <h5 className="mb-0">
                                    <i className="fas fa-tools me-2"></i>
                                    Quick Actions
                                </h5>
                            </Card.Header>
                            <Card.Body>
                                {renderActionCards()}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>

            {/* Open Container Confirmation Modal */}
            <Modal show={showOpenModal} onHide={() => setShowOpenModal(false)} centered>
                <Modal.Header closeButton className="bg-warning text-dark">
                    <Modal.Title>
                        <i className="fas fa-door-open me-2"></i>
                        Confirm Container Opening
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="text-center">
                        <i className="fas fa-box-open fa-3x text-warning mb-3"></i>
                        <h5>Are you sure you want to open the container?</h5>
                        <p className="text-muted">
                            This action will open the container for transaction ID: <strong>{formData.transactionId}</strong>
                        </p>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowOpenModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="warning" onClick={confirmOpenContainer} disabled={isSubmitting}>
                        {isSubmitting ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Opening...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-unlock me-2"></i>
                                Open Container
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Refund Confirmation Modal */}
            <Modal show={showRefundModal} onHide={() => setShowRefundModal(false)} centered>
                <Modal.Header closeButton className="bg-danger text-white">
                    <Modal.Title>
                        <i className="fas fa-undo me-2"></i>
                        Confirm Refund
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="text-center">
                        <i className="fas fa-money-bill-wave fa-3x text-danger mb-3"></i>
                        <h5>Are you sure you want to process a refund?</h5>
                        <p className="text-muted">
                            This action will process a refund for transaction ID: <strong>{formData.transactionId}</strong>
                        </p>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowRefundModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={confirmRefundCan} disabled={isSubmitting}>
                        {isSubmitting ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Processing...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-money-bill-wave me-2"></i>
                                Process Refund
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Toast Notifications */}
            <ToastContainer position="top-end" className="p-3">
                <Toast 
                    show={showToast} 
                    onClose={() => setShowToast(false)} 
                    delay={3000} 
                    autohide
                    bg={toastVariant}
                >
                    <Toast.Header>
                        <i className={`fas ${toastVariant === 'success' ? 'fa-check-circle' : 'fa-exclamation-triangle'} me-2`}></i>
                        <strong className="me-auto">
                            {toastVariant === 'success' ? 'Success' : 'Error'}
                        </strong>
                    </Toast.Header>
                    <Toast.Body className={toastVariant === 'success' ? 'text-white' : 'text-white'}>
                        {toastMessage}
                    </Toast.Body>
                </Toast>
            </ToastContainer>
        </Fragment>
    )

}

Transaction.propTypes = {
    getTransaction: PropTypes.func.isRequired,
    updateTransaction: PropTypes.func.isRequired,
    openContainer: PropTypes.func.isRequired,
    refundCan: PropTypes.func.isRequired,
    transaction: PropTypes.object,
    loading: PropTypes.bool
} 
const mapStateToProps = state => ({
    transaction: state.canTransaction.transaction,
    loading: state.canTransaction.loading
})

export default connect( mapStateToProps, { getTransaction, updateTransaction, openContainer, refundCan } )( Transaction );